import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Calendar,
  Clock,
  MapPin,
  Users,
  Luggage,
  Baby,
  Plane,
  Phone,
  Mail,
  User,
  CheckCircle,
  XCircle,
  Clock as ClockIcon,
  RefreshCw,
  Printer,
  DollarSign,
  Hash,
  Building,
  PlaneTakeoff,
  Info,
  Edit,
  ExternalLink,
  Send,
  Banknote,
  CreditCard,
} from "lucide-react";
import { bookingsApi } from "../api";
import { Booking } from "../types";

const BookingDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [booking, setBooking]: any = useState<Booking | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [updating, setUpdating] = useState(false);
  const [sendingGJ, setSendingGJ] = useState(false);
  const [gjSuccess, setGjSuccess] = useState<string | null>(null);
  const [gjError, setGjError] = useState<string | null>(null);
  const [showGJResponse, setShowGJResponse] = useState(false);

  const handleSendToGoodJourney = async () => {
    if (!booking?.id) return;
    try {
      setSendingGJ(true);
      setGjSuccess(null);
      setGjError(null);
      const response = await bookingsApi.sendToGoodJourney(booking.id);
      setGjSuccess(response.data.message || "Sent successfully to Good Journey!");
      await fetchBooking();
    } catch (err: any) {
      setGjError(
        err.response?.data?.message || "Failed to send to Good Journey"
      );
      console.error("Failed to send to Good Journey:", err);
    } finally {
      setSendingGJ(false);
    }
  };

  const fetchBooking = async () => {
    if (!id) {
      setError("Booking ID is missing");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError("");
      const response = await bookingsApi.getById(id);
      const data = response.data.data;
      setBooking({
        ...data,
        id: data._id || data.id,
      });
    } catch (err: any) {
      setError(
        err.response?.data?.message || "Failed to fetch booking details",
      );
      console.error("Failed to fetch booking:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBooking();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleStatusUpdate = async (newStatus: string) => {
    if (!booking?.id) return;

    try {
      setUpdating(true);
      await bookingsApi.updateStatus(booking.id, newStatus);
      await fetchBooking();
    } catch (err: any) {
      setError(
        err.response?.data?.message || "Failed to update booking status",
      );
      console.error("Failed to update booking status:", err);
    } finally {
      setUpdating(false);
    }
  };

  const getStatusConfig = (status: string) => {
    const configs = {
      confirmed: {
        color: "bg-emerald-50 text-emerald-700 border-emerald-200",
        icon: <CheckCircle className="w-6 h-6" />,
        message: "Booking confirmed and ready",
      },
      pending: {
        color: "bg-amber-50 text-amber-700 border-amber-200",
        icon: <ClockIcon className="w-6 h-6" />,
        message: "Awaiting confirmation",
      },
      cancelled: {
        color: "bg-rose-50 text-rose-700 border-rose-200",
        icon: <XCircle className="w-6 h-6" />,
        message: "Booking cancelled",
      },
      completed: {
        color: "bg-blue-50 text-blue-700 border-blue-200",
        icon: <CheckCircle className="w-6 h-6" />,
        message: "Trip completed",
      },
    };
    return configs[status as keyof typeof configs] || configs.pending;
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatDateTime = (date: string, time?: string) => {
    if (!date) return "N/A";
    const formattedDate = formatDate(date);
    return time ? `${formattedDate} at ${time}` : formattedDate;
  };

  const statusConfig = booking
    ? getStatusConfig(booking.status)
    : getStatusConfig("pending");

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-primary border-t-transparent mx-auto"></div>
          <p className="mt-6 text-gray-500 font-medium">
            Loading booking details...
          </p>
        </div>
      </div>
    );
  }

  if (error || !booking) {
    return (
      <div className="text-center py-16">
        <div className="text-6xl mb-4">🔍</div>
        <p className="text-gray-700 text-lg font-medium">
          {error || "Booking not found"}
        </p>
        <button
          onClick={() => navigate("/bookings")}
          className="mt-4 text-primary hover:underline font-medium"
        >
          Back to Bookings
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate("/bookings")}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Booking Details
            </h1>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-sm text-gray-500">Reference:</span>
              <span className="font-mono font-semibold text-gray-900 bg-gray-100 px-3 py-1 rounded-lg">
                {booking.bookingReference}
              </span>
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => window.print()}
            className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <Printer className="w-4 h-4" />
            Print
          </button>
          <button
            onClick={fetchBooking}
            className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>
      </div>

      {/* Status Card */}
      <div className={`p-6 rounded-xl border-2 ${statusConfig.color}`}>
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div className="flex items-center gap-4">
            <div className="p-2 bg-white/50 rounded-full">
              {statusConfig.icon}
            </div>
            <div>
              <h2 className="text-xl font-bold capitalize">{booking.status}</h2>
              <p className="text-sm opacity-75">{statusConfig.message}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Payment Method Banner */}
      {((booking as any).paymentMethod === "cash" || (booking as any).isCashPayment) ? (
        <div className="p-5 rounded-xl border-2 border-green-300 bg-gradient-to-r from-green-50 to-emerald-50">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-green-100 rounded-full">
                <Banknote className="w-6 h-6 text-green-700" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-green-900 flex items-center gap-2">
                  💵 Cash Payment Booking
                </h2>
                <p className="text-sm text-green-700 mt-0.5">Collect cash from client upon arrival</p>
              </div>
            </div>
            <span className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-bold bg-green-200 text-green-900 border border-green-300">
              CASH PAYMENT
            </span>
          </div>
          {(booking as any).internalNotes && (
            <div className="mt-4 p-3 bg-white/60 rounded-lg border border-green-200">
              <p className="text-xs text-green-600 font-semibold uppercase tracking-wide mb-1">Internal Notes</p>
              <p className="text-sm text-green-800 font-medium">{(booking as any).internalNotes}</p>
            </div>
          )}
        </div>
      ) : (
        <div className="p-5 rounded-xl border-2 border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-blue-100 rounded-full">
              <CreditCard className="w-6 h-6 text-blue-700" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-blue-900 flex items-center gap-2">
                💳 Online Payment Booking
              </h2>
              <p className="text-sm text-blue-700 mt-0.5">Paid via Stripe</p>
            </div>
            <span className="ml-auto inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-bold bg-blue-200 text-blue-900 border border-blue-300">
              ONLINE PAYMENT
            </span>
          </div>
        </div>
      )}

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column */}
        <div className="lg:col-span-1 space-y-6">
          {/* Customer Info */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <User className="w-5 h-5 text-primary" />
              Customer Information
            </h3>
            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-500 font-medium">Full Name</p>
                <p className="font-semibold text-gray-900">
                  {booking.customerName}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500 font-medium">
                  Email Address
                </p>
                <a
                  href={`mailto:${booking.customerEmail}`}
                  className="text-primary hover:underline flex items-center gap-2 font-medium"
                >
                  <Mail className="w-4 h-4" />
                  {booking.customerEmail}
                </a>
              </div>
              <div>
                <p className="text-sm text-gray-500 font-medium">
                  Phone Number
                </p>
                <a
                  href={`tel:${booking.customerPhone}`}
                  className="text-primary hover:underline flex items-center gap-2 font-medium"
                >
                  <Phone className="w-4 h-4" />
                  {booking.customerPhone}
                </a>
              </div>
            </div>
          </div>

          {/* Booking Summary */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Booking Summary
            </h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center pb-2 border-b border-gray-100">
                <span className="text-gray-500">Total Price</span>
                <span className="text-2xl font-bold text-primary flex items-center gap-1">
                  <DollarSign className="w-5 h-5" />
                  {booking.totalPrice}
                </span>
              </div>
              <div className="flex justify-between items-center pb-2 border-b border-gray-100">
                <span className="text-gray-500">Payment Method</span>
                {(booking as any).paymentMethod === "cash" || (booking as any).isCashPayment ? (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-green-100 text-green-800 border border-green-200">
                    <Banknote className="w-3.5 h-3.5" />
                    CASH
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-blue-100 text-blue-800 border border-blue-200">
                    <CreditCard className="w-3.5 h-3.5" />
                    ONLINE
                  </span>
                )}
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500 flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Persons
                </span>
                <span className="font-medium">{booking.persons}</span>
              </div>
              {booking.flightNumber && (
                <div className="flex justify-between">
                  <span className="text-gray-500 flex items-center gap-2">
                    <Hash className="w-4 h-4" />
                    Flight Number
                  </span>
                  <span className="font-mono font-medium">
                    {booking.flightNumber}
                  </span>
                </div>
              )}
              {booking.roomNumber && (
                <div className="flex justify-between">
                  <span className="text-gray-500 flex items-center gap-2">
                    <Building className="w-4 h-4" />
                    Room Number
                  </span>
                  <span className="font-medium">{booking.roomNumber}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right Column */}
        <div className="lg:col-span-2 space-y-6">
          {booking.bookingType === "hotel-check" ? (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Building className="w-5 h-5 text-primary" />
                Hotel Inquiry Details
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-3">
                  <div>
                    <p className="text-sm text-gray-500">Destinations</p>
                    <p className="font-semibold text-gray-900">{booking.destination || "Egypt"}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Dates</p>
                    <p className="font-semibold text-gray-900">
                      {booking.arrivalDate} to {booking.departureDate} ({booking.nights || "N/A"} Nights)
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Flexible Dates</p>
                    <p className="font-semibold text-gray-900">{booking.flexibleDates || "Ja"}</p>
                  </div>
                  {booking.departureAirports && (
                    <div>
                      <p className="text-sm text-gray-500">Departure Airports</p>
                      <p className="font-semibold text-gray-900">{booking.departureAirports}</p>
                    </div>
                  )}
                </div>

                <div className="space-y-3">
                  <div>
                    <p className="text-sm text-gray-500">Total Persons</p>
                    <p className="font-semibold text-gray-900">{booking.persons} Persons</p>
                  </div>
                  {booking.interestedInExcursions && (
                    <div>
                      <p className="text-sm text-gray-500">Interested in Excursions</p>
                      <p className="font-semibold text-gray-900">{booking.interestedInExcursions}</p>
                    </div>
                  )}
                  {booking.notes && (
                    <div className="col-span-2">
                      <p className="text-sm text-gray-500">Special Notes / Comments</p>
                      <p className="font-medium text-gray-900 whitespace-pre-wrap mt-1 p-3 bg-gray-50 rounded-lg border border-gray-100">
                        {booking.notes}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {booking.preferences && booking.preferences.length > 0 && (
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <h4 className="font-semibold text-gray-700 mb-3">Hotel Preferences</h4>
                  <div className="flex flex-wrap gap-2">
                    {booking.preferences.map((pref: string, idx: number) => (
                      <span key={idx} className="bg-purple-50 text-purple-700 px-3 py-1 rounded-full text-xs font-semibold border border-purple-200">
                        ✨ {pref}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Metadata */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-gray-500">Created</p>
                    <p className="font-medium text-gray-700">
                      {new Date(
                        booking.createdAt || booking.created_at || Date.now(),
                      ).toLocaleString()}
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-gray-500">Last Updated</p>
                    <p className="font-medium text-gray-700">
                      {new Date(
                        booking.updatedAt || booking.updated_at || Date.now(),
                      ).toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            /* Trip Details */
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Plane className="w-5 h-5 text-primary" />
                Trip Details
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Arrival */}
                <div className="bg-gradient-to-br from-gray-50 to-white rounded-lg p-5 border border-gray-100">
                  <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <PlaneTakeoff className="w-4 h-4 text-primary" />
                    Arrival
                  </h4>
                  <div className="space-y-3">
                    <div>
                      <p className="text-sm text-gray-500">Airport</p>
                      <p className="font-medium">
                        {booking.arrivalAirport || "Hurghada Airport"}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Destination</p>
                      <p className="font-medium flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-primary" />
                        {booking.destination}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Date & Time</p>
                      <p className="font-medium flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-primary" />
                        {formatDateTime(booking.arrivalDate, booking.arrivalTime)}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Return */}
                {booking.departureDate && (
                  <div className="bg-gradient-to-br from-gray-50 to-white rounded-lg p-5 border border-gray-100">
                    <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                      <Clock className="w-4 h-4 text-primary" />
                      Return
                    </h4>
                    <div className="space-y-3">
                      <div>
                        <p className="text-sm text-gray-500">Departure Date</p>
                        <p className="font-medium flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-primary" />
                          {formatDateTime(
                            booking.departureDate,
                            booking.departureTime,
                          )}
                        </p>
                      </div>
                      {booking.pickupTime && (
                        <div>
                          <p className="text-sm text-gray-500">Pickup Time</p>
                          <p className="font-medium flex items-center gap-2">
                            <Clock className="w-4 h-4 text-primary" />
                            {booking.pickupTime}
                            {booking.pickupAdjustment !== undefined &&
                              booking.pickupAdjustment !== 0 && (
                                <span className="text-sm text-gray-500">
                                  ({booking.pickupAdjustment > 0 ? "+" : ""}
                                  {booking.pickupAdjustment} min)
                                </span>
                              )}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Luggage */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                <h4 className="font-semibold text-gray-700 mb-4 flex items-center gap-2">
                  <Luggage className="w-4 h-4" />
                  Luggage Details
                </h4>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div className="bg-gradient-to-br from-gray-50 to-white rounded-lg p-4 text-center border border-gray-100">
                    <p className="text-2xl font-bold text-primary">
                      {booking.largeSuitcases || 0}
                    </p>
                    <p className="text-sm text-gray-500">Large Suitcases</p>
                  </div>
                  <div className="bg-gradient-to-br from-gray-50 to-white rounded-lg p-4 text-center border border-gray-100">
                    <p className="text-2xl font-bold text-primary">
                      {booking.smallSuitcases || 0}
                    </p>
                    <p className="text-sm text-gray-500">Small Suitcases</p>
                  </div>
                  {booking.childSeat !== undefined && booking.childSeat > 0 && (
                    <div className="bg-gradient-to-br from-gray-50 to-white rounded-lg p-4 text-center border border-gray-100">
                      <p className="text-2xl font-bold text-primary flex items-center justify-center gap-1">
                        <Baby className="w-5 h-5" />
                        {booking.childSeat}
                      </p>
                      <p className="text-sm text-gray-500">Child Seats</p>
                    </div>
                  )}
                  {booking.babySeat !== undefined && booking.babySeat > 0 && (
                    <div className="bg-gradient-to-br from-gray-50 to-white rounded-lg p-4 text-center border border-gray-100">
                      <p className="text-2xl font-bold text-primary flex items-center justify-center gap-1">
                        <Baby className="w-5 h-5" />
                        {booking.babySeat}
                      </p>
                      <p className="text-sm text-gray-500">Baby Seats</p>
                    </div>
                  )}
                  {booking.otherLuggage && (
                    <div className="bg-gradient-to-br from-gray-50 to-white rounded-lg p-4 text-center border border-gray-100 col-span-2 sm:col-span-1">
                      <p className="text-sm text-gray-500">Other</p>
                      <p className="font-medium">{booking.otherLuggage}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Metadata */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-gray-500">Created</p>
                    <p className="font-medium text-gray-700">
                      {new Date(
                        booking.createdAt || booking.created_at || Date.now(),
                      ).toLocaleString()}
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-gray-500">Last Updated</p>
                    <p className="font-medium text-gray-700">
                      {new Date(
                        booking.updatedAt || booking.updated_at || Date.now(),
                      ).toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BookingDetail;